import { useEffect, useMemo, useRef, useState } from "react";
import { MODELS_PATHS } from "pcs-shared-code/dropmarking/utils/constants";

const SITE_ID_ALIASES = {
  "Canada Water - Three Deal Porters": "Canada Water-Three Deal Porters",
};

const withTimeout = (promise, timeoutMs, timeoutMessage) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(timeoutMessage));
      }, timeoutMs);
    }),
  ]);
};

const resolveModelKey = (normalizedSiteId, preferLowPoly) => {
  if (!preferLowPoly) {
    return normalizedSiteId;
  }

  const lowPolyCandidates = [
    `${normalizedSiteId}_LOW`,
    `${normalizedSiteId}_low`,
    `${normalizedSiteId}-LOW`,
    `${normalizedSiteId}-low`,
  ];

  const lowPolyKey = lowPolyCandidates.find((candidate) => MODELS_PATHS[candidate]);
  return lowPolyKey || normalizedSiteId;
};

export function useBuildingModel(siteId, onError, options = {}) {
  const [modelPath, setModelPath] = useState("");
  const mountedRef = useRef(true);
  const { preferLowPoly = false, timeoutMs = 22000 } = options;

  const normalizedSiteId = useMemo(() => {
    const rawSiteId = typeof siteId === "string" ? siteId.trim() : "";
    if (!rawSiteId) {
      return "";
    }
    return SITE_ID_ALIASES[rawSiteId] || rawSiteId;
  }, [siteId]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const resolveModelPath = async () => {
      setModelPath("");

      if (!normalizedSiteId) {
        onError?.(new Error("Building is missing a valid site id."));
        return;
      }

      const modelKey = resolveModelKey(normalizedSiteId, preferLowPoly);
      const relativeModelPath = MODELS_PATHS[modelKey];
      if (!relativeModelPath) {
        onError?.(
          new Error(`No 3D model is configured for \"${normalizedSiteId}\".`)
        );
        return;
      }

      try {
        const module = await withTimeout(
          import(`pcs-shared-code/dropmarking/DropmarkingBuildings/${relativeModelPath}`),
          timeoutMs,
          "3D model loading took too long. Try Low Performance Mode."
        );

        if (cancelled || !mountedRef.current) {
          return;
        }

        const resolvedPath = module?.default || "";
        if (!resolvedPath) {
          onError?.(
            new Error(`Model import did not return a path for \"${normalizedSiteId}\".`)
          );
          return;
        }

        setModelPath(resolvedPath);
      } catch (error) {
        if (!cancelled && mountedRef.current) {
          onError?.(error);
        }
      }
    };

    resolveModelPath();

    return () => {
      cancelled = true;
    };
  }, [normalizedSiteId, onError, preferLowPoly, timeoutMs]);

  return { modelPath, normalizedSiteId };
}
